import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useApp } from "@/contexts/AppContext";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Wifi,
  ShieldCheck,
  CreditCard,
  ArrowRight,
  Loader2,
  RefreshCw,
  ChevronDown,
  Package,
  Globe,
  Radio,
  Lock,
  ExternalLink,
} from "lucide-react";
import apiClient from "@/api/client";

// ─── Types ─────────────────────────────────────────────────────────────────
interface LifecycleStep {
  complete: boolean;
  label: string;
}

interface LifecycleData {
  registration: {
    registrationId: string;
    status: string;
    step1Complete: boolean;
    step2Complete: boolean;
    step3Complete: boolean;
    approved: boolean;
    rejectionReason: string | null;
    expiresAt?: string;
    updatedAt: string;
  } | null;
  hotspot: {
    id: string;
    name?: string;
    ssid?: string;
    ssidConfigured: boolean;
    status: "online" | "offline" | "maintenance";
    online: boolean;
    lastSeen: string | null;
    secondsSinceLastSeen: number | null;
  } | null;
  steps: Record<"1" | "2" | "3" | "4" | "5" | "6", LifecycleStep>;
}

type StepNumber = 1 | 2 | 3 | 4 | 5 | 6;

// ─── Helpers ───────────────────────────────────────────────────────────────
function formatRelativeTime(seconds: number | null): string {
  if (seconds === null) return "never";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}


// ─── Inline SSID Editor (Setup Guide Step 2) ───────────────────────────────
interface SsidEditorProps {
  hotspotId: string;
  currentSsid?: string;
  theme: string;
  onSaved: () => void;
}

const SSID_PREFIX = "KonectX.net-";
const SSID_CUSTOM_PATTERN = /^[A-Za-z0-9]{1,20}$/;

function SsidEditor({ hotspotId, currentSsid, theme, onSaved }: SsidEditorProps) {
  const { t } = useTranslation();

  // Pre-fill custom part if existing SSID already matches the pattern
  const initialCustom =
    currentSsid && currentSsid.startsWith(SSID_PREFIX)
      ? currentSsid.slice(SSID_PREFIX.length)
      : "";

  const [customPart, setCustomPart] = useState(initialCustom);
  const [checkResult, setCheckResult] = useState<
    { available: boolean; reason?: string } | null
  >(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const fullSsid = SSID_PREFIX + customPart;
  const patternValid = SSID_CUSTOM_PATTERN.test(customPart);
  const isUnchanged = currentSsid === fullSsid;

  // Reset check result when user types
  const handleChange = (value: string) => {
    // Strip the prefix if user pasted the whole thing
    let v = value;
    if (v.startsWith(SSID_PREFIX)) v = v.slice(SSID_PREFIX.length);
    // Strip non-alphanumeric and cap at 20
    v = v.replace(/[^A-Za-z0-9]/g, "").slice(0, 20);
    setCustomPart(v);
    setCheckResult(null);
    setSaveError(null);
    setSaved(false);
  };

  const checkAvailability = async () => {
    if (!patternValid) return;
    setChecking(true);
    setCheckResult(null);
    try {
      const resp = await apiClient.get<{ success: boolean; available: boolean; reason?: string }>(
        `/hotspots/${hotspotId}/ssid/check`,
        { params: { name: fullSsid } }
      );
      setCheckResult({ available: !!resp.data?.available, reason: resp.data?.reason });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      setCheckResult({
        available: false,
        reason:
          axiosErr.response?.data?.message ||
          t("operator.setupGuide.ssid.checkFailed", { defaultValue: "Could not check availability" }),
      });
    } finally {
      setChecking(false);
    }
  };

  const saveSsid = async () => {
    if (!patternValid || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await apiClient.post(`/hotspots/${hotspotId}/ssid`, { ssid: fullSsid });
      setSaved(true);
      // Refresh lifecycle data so the green check appears
      setTimeout(() => onSaved(), 500);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      setSaveError(
        axiosErr.response?.data?.message ||
          t("operator.setupGuide.ssid.saveFailed", { defaultValue: "Failed to save SSID" })
      );
    } finally {
      setSaving(false);
    }
  };

  const inputBase =
    theme === "dark"
      ? "bg-gray-900 border-gray-700 text-white placeholder-gray-600"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400";

  return (
    <div className="mt-4 space-y-3">
      {/* Input row */}
      <div>
        <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${
          theme === "dark" ? "text-gray-400" : "text-gray-600"
        }`}>
          {t("operator.setupGuide.ssid.newNameLabel", { defaultValue: "New hotspot name" })}
        </label>
        <div className={`flex items-stretch rounded-lg border overflow-hidden ${
          theme === "dark" ? "border-gray-700" : "border-gray-300"
        }`}>
          <span className={`px-3 flex items-center text-sm font-mono select-none ${
            theme === "dark" ? "bg-gray-800 text-gray-400" : "bg-gray-100 text-gray-600"
          }`}>
            {SSID_PREFIX}
          </span>
          <input
            type="text"
            value={customPart}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="LagosHub"
            maxLength={20}
            className={`flex-1 px-3 py-2 text-sm font-mono outline-none border-0 ${inputBase}`}
          />
        </div>
        <p className={`text-xs mt-1 ${theme === "dark" ? "text-gray-500" : "text-gray-500"}`}>
          {customPart.length}/20 {t("operator.setupGuide.ssid.alphanumericOnly", { defaultValue: "alphanumeric characters" })}
        </p>
      </div>

      {/* Validation status */}
      <ul className="space-y-1">
        <li className="flex items-center gap-2 text-xs">
          <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
            customPart.length > 0 ? "bg-green-500" : theme === "dark" ? "bg-gray-700" : "bg-gray-200"
          }`}>
            {customPart.length > 0 && <CheckCircle2 className="w-3 h-3 text-white" />}
          </span>
          <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>
            {t("operator.setupGuide.ssid.v1", { defaultValue: "Starts with KonectX.net-" })}
          </span>
        </li>
        <li className="flex items-center gap-2 text-xs">
          <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
            patternValid ? "bg-green-500" : theme === "dark" ? "bg-gray-700" : "bg-gray-200"
          }`}>
            {patternValid && <CheckCircle2 className="w-3 h-3 text-white" />}
          </span>
          <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>
            {t("operator.setupGuide.ssid.v2", { defaultValue: "Custom part is 1–20 alphanumeric characters" })}
          </span>
        </li>
        <li className="flex items-center gap-2 text-xs">
          <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
            checkResult?.available ? "bg-green-500" : theme === "dark" ? "bg-gray-700" : "bg-gray-200"
          }`}>
            {checkResult?.available && <CheckCircle2 className="w-3 h-3 text-white" />}
          </span>
          <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>
            {t("operator.setupGuide.ssid.v3", { defaultValue: "Name is unique across the network" })}
          </span>
          {checkResult && !checkResult.available && checkResult.reason && (
            <span className="text-red-500 ml-1">— {checkResult.reason}</span>
          )}
        </li>
        <li className="flex items-center gap-2 text-xs">
          <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
            saved ? "bg-green-500" : theme === "dark" ? "bg-gray-700" : "bg-gray-200"
          }`}>
            {saved && <CheckCircle2 className="w-3 h-3 text-white" />}
          </span>
          <span className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>
            {t("operator.setupGuide.ssid.v4", { defaultValue: "Saved and applied to your hotspot" })}
          </span>
        </li>
      </ul>

      {/* Save error */}
      {saveError && (
        <div className={`text-xs px-3 py-2 rounded-lg ${
          theme === "dark" ? "bg-red-900/20 text-red-300 border border-red-800" : "bg-red-50 text-red-700 border border-red-200"
        }`}>
          {saveError}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          onClick={checkAvailability}
          disabled={!patternValid || checking || saving}
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            theme === "dark"
              ? "bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700"
              : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
          }`}
        >
          {checking ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" />
              {t("operator.setupGuide.ssid.checking", { defaultValue: "Checking..." })}
            </span>
          ) : (
            t("operator.setupGuide.ssid.check", { defaultValue: "Check Availability" })
          )}
        </button>
        <button
          type="button"
          onClick={saveSsid}
          disabled={!patternValid || saving || isUnchanged || saved}
          className="text-xs font-semibold px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
        >
          {saving ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              {t("operator.setupGuide.ssid.saving", { defaultValue: "Saving..." })}
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-3 h-3" />
              {t("operator.setupGuide.ssid.savedLabel", { defaultValue: "Saved" })}
            </>
          ) : isUnchanged ? (
            t("operator.setupGuide.ssid.noChange", { defaultValue: "No Change" })
          ) : (
            <>
              {t("operator.setupGuide.ssid.save", { defaultValue: "Save SSID" })}
              <ArrowRight className="w-3 h-3" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────
export default function SetupGuide() {
  const { theme, user } = useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LifecycleData | null>(null);
  const [expandedStep, setExpandedStep] = useState<StepNumber | null>(null);

  const fetchLifecycle = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get("/registerkit/lifecycle");
      setData(response.data?.data ?? null);
    } catch (err: unknown) {
      console.error("Failed to fetch lifecycle:", err);
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      setError(
        axiosErr.response?.data?.message ||
          axiosErr.message ||
          t("operator.setupGuide.errors.loadFailed", {
            defaultValue: "Failed to load your setup status",
          })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLifecycle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-expand the first incomplete step on first load
  useEffect(() => {
    if (!data || expandedStep !== null) return;
    const firstIncomplete = ([1, 2, 3, 4, 5, 6] as StepNumber[]).find(
      (n) => !data.steps[String(n) as keyof typeof data.steps].complete
    );
    setExpandedStep(firstIncomplete ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // ─── Progress math ───────────────────────────────────────────────────────
  const { completedCount, totalSteps, percentage } = useMemo(() => {
    if (!data) return { completedCount: 0, totalSteps: 6, percentage: 0 };
    const stepsArr = Object.values(data.steps);
    const done = stepsArr.filter((s) => s.complete).length;
    return {
      completedCount: done,
      totalSteps: stepsArr.length,
      percentage: Math.round((done / stepsArr.length) * 100),
    };
  }, [data]);

  // ─── Step definitions (icons, sub-checklists, actions) ───────────────────
  const stepConfig: Record<
    StepNumber,
    {
      icon: typeof Wifi;
      title: string;
      description: string;
      checklist: string[];
      note?: string;
      optional?: boolean;
      action?: { label: string; onClick: () => void };
    }
  > = useMemo(
    () => ({
      1: {
        icon: Package,
        title: t("operator.setupGuide.steps.1.title", {
          defaultValue: "Register Your Hotspot Kit",
        }),
        description: t("operator.setupGuide.steps.1.desc", {
          defaultValue:
            "Add your hotspot to the KonectX network — device details, identity verification, and activation fee.",
        }),
        checklist: [
          t("operator.setupGuide.steps.1.c1", { defaultValue: "Submit device details (MAC address, serial)" }),
          t("operator.setupGuide.steps.1.c2", { defaultValue: "Complete identity verification (KYC)" }),
          t("operator.setupGuide.steps.1.c3", { defaultValue: "Pay activation fee" }),
          t("operator.setupGuide.steps.1.c4", { defaultValue: "Wait for admin approval" }),
        ],
        action: !data?.registration
          ? { label: t("operator.setupGuide.cta.start", { defaultValue: "Start Registration" }), onClick: () => navigate("/operator/registerKit") }
          : data.registration.status === "rejected"
          ? { label: t("operator.setupGuide.cta.restart", { defaultValue: "Start New Registration" }), onClick: () => navigate("/operator/registerKit") }
          : !data.registration.approved
          ? { label: t("operator.setupGuide.cta.continue", { defaultValue: "Continue Registration" }), onClick: () => {
              const step = !data.registration!.step1Complete ? "/operator/registerKit"
                : !data.registration!.step2Complete ? "/operator/registerKitStep2"
                : "/operator/registerKitStep3";
              navigate(step);
            } }
          : undefined,
      },
      2: {
        icon: Radio,
        title: t("operator.setupGuide.steps.2.title", { defaultValue: "Configure Hotspot Name (SSID)" }),
        description: t("operator.setupGuide.steps.2.desc", {
          defaultValue:
            "Set the public name your hotspot will broadcast. Must follow the KonectX naming convention so users can trust it.",
        }),
        note: "KonectX.net-LagosHub • KonectX.net-CityMall",
        checklist: [
          t("operator.setupGuide.steps.2.c1", { defaultValue: "Name starts with KonectX.net-" }),
          t("operator.setupGuide.steps.2.c2", { defaultValue: "Custom part is 1–20 alphanumeric characters" }),
          t("operator.setupGuide.steps.2.c3", { defaultValue: "Name is unique across the network" }),
          t("operator.setupGuide.steps.2.c4", { defaultValue: "Saved and applied to your hotspot" }),
        ],
        // Step 2 has no navigate action — the SSID editor is rendered inline
        // inside the expanded step body (see SsidEditor component below).
        action: undefined,
      },
      3: {
        icon: Globe,
        title: t("operator.setupGuide.steps.3.title", { defaultValue: "Connect to the Internet" }),
        description: t("operator.setupGuide.steps.3.desc", {
          defaultValue:
            "Plug your hotspot into a stable internet source. The system detects activity automatically — once users start connecting through your hotspot, this step completes.",
        }),
        checklist: [
          t("operator.setupGuide.steps.3.c1", { defaultValue: "Connect uplink (fiber / 4G/5G / ethernet)" }),
          t("operator.setupGuide.steps.3.c2", { defaultValue: "Hotspot has stable internet access" }),
          t("operator.setupGuide.steps.3.c3", { defaultValue: "Recommended uptime: 95%+" }),
          t("operator.setupGuide.steps.3.c4", { defaultValue: "Detected automatically via captive portal traffic" }),
        ],
      },
      4: {
        icon: Wifi,
        title: t("operator.setupGuide.steps.4.title", { defaultValue: "Hotspot Online" }),
        description: t("operator.setupGuide.steps.4.desc", {
          defaultValue:
            "Once your hotspot is broadcasting and accepting connections, it shows as live on the KonectX network.",
        }),
        checklist: [
          t("operator.setupGuide.steps.4.c1", { defaultValue: "Hotspot reachable on the network" }),
          t("operator.setupGuide.steps.4.c2", { defaultValue: "Heartbeat seen within last 5 minutes" }),
          t("operator.setupGuide.steps.4.c3", { defaultValue: "Visible on the KonectX coverage map" }),
          t("operator.setupGuide.steps.4.c4", { defaultValue: "Ready to accept end-user connections" }),
        ],
      },
      5: {
        icon: ShieldCheck,
        title: t("operator.setupGuide.steps.5.title", { defaultValue: "Identity Verification (KYC)" }),
        description: t("operator.setupGuide.steps.5.desc", {
          defaultValue:
            "KYC is completed during kit registration. It unlocks higher payout limits and protects the network.",
        }),
        checklist: [
          t("operator.setupGuide.steps.5.c1", { defaultValue: "Government-issued ID uploaded" }),
          t("operator.setupGuide.steps.5.c2", { defaultValue: "Selfie / liveness check completed" }),
          t("operator.setupGuide.steps.5.c3", { defaultValue: "Proof of address submitted" }),
          t("operator.setupGuide.steps.5.c4", { defaultValue: "Verification approved" }),
        ],
      },
      6: {
        icon: CreditCard,
        title: t("operator.setupGuide.steps.6.title", { defaultValue: "Add Payout Details" }),
        description: t("operator.setupGuide.steps.6.desc", {
          defaultValue:
            "Configure how you'll receive earnings from your hotspot. This is set up on the Token page when you make your first withdrawal.",
        }),
        optional: true,
        checklist: [
          t("operator.setupGuide.steps.6.c1", { defaultValue: "Visit the Token page" }),
          t("operator.setupGuide.steps.6.c2", { defaultValue: "Choose payout method (wallet / bank)" }),
          t("operator.setupGuide.steps.6.c3", { defaultValue: "Verify your account details" }),
          t("operator.setupGuide.steps.6.c4", { defaultValue: "Submit your first withdrawal when ready" }),
        ],
        action: { label: t("operator.setupGuide.cta.goToToken", { defaultValue: "Go to Token page" }), onClick: () => navigate("/operator/Token") },
      },
    }),
    [t, data, navigate]
  );

  // ─── Loading ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const displayName = user?.firstName || (user?.name ? user.name.split(' ')[0] : null);
  const greeting = displayName
    ? t("operator.setupGuide.greetingNamed", {
        defaultValue: "Welcome to KonectX, {{name}}",
        name: displayName,
      })
    : t("operator.setupGuide.greeting", { defaultValue: "Welcome to KonectX" });

  return (
    <>
      <Helmet>
        <title>{t("operator.setupGuide.metaTitle", { defaultValue: "Setup Guide | KonectX" })}</title>
        <meta
          name="description"
          content={t("operator.setupGuide.metaDescription", {
            defaultValue: "Get your hotspot online and earning in 6 simple steps.",
          })}
        />
      </Helmet>

      <div className={`min-h-screen px-4 sm:px-8 py-8 ${theme === "dark" ? "bg-black" : "bg-gray-50"}`}>
        <div className="max-w-5xl mx-auto">
          {/* ─── Header ─────────────────────────────────────────────── */}
          <div className="mb-6">
            <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              {greeting}
            </h1>
            <p className={`text-sm sm:text-base ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
              {t("operator.setupGuide.subtitle", {
                defaultValue: "Get your hotspot online and earning in 6 simple steps.",
              })}
            </p>
            <p className={`text-xs mt-1 ${theme === "dark" ? "text-gray-500" : "text-gray-500"}`}>
              {t("operator.setupGuide.estimatedTime", { defaultValue: "Estimated setup time: 10–15 minutes" })}
            </p>
          </div>

          {/* ─── Error banner ───────────────────────────────────────── */}
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4 flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-700 dark:text-red-300">{error}</p>
              </div>
              <button
                onClick={fetchLifecycle}
                className="text-sm font-medium text-red-700 dark:text-red-400 hover:underline flex items-center gap-1"
              >
                <RefreshCw className="w-4 h-4" />
                {t("operator.setupGuide.retry", { defaultValue: "Retry" })}
              </button>
            </div>
          )}

          {/* ─── Progress card ──────────────────────────────────────── */}
          <div
            className={`rounded-2xl border p-5 mb-6 ${
              theme === "dark" ? "bg-[#333436] border-gray-800" : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                  {t("operator.setupGuide.overallProgress", { defaultValue: "Overall Progress" })}
                </p>
                <p className={`text-2xl font-bold ${percentage === 100 ? "text-green-500" : theme === "dark" ? "text-white" : "text-gray-900"}`}>
                  {percentage}%
                  {percentage === 100 && <span className="ml-2">🎉</span>}
                </p>
              </div>
              <div className="text-right">
                <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                  {t("operator.setupGuide.stepsDone", { defaultValue: "Steps Done" })}
                </p>
                <p className={`text-2xl font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                  {completedCount}
                  <span className={`text-sm font-normal ml-1 ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>
                    / {totalSteps}
                  </span>
                </p>
              </div>
            </div>

            <div className={`h-2 rounded-full overflow-hidden ${theme === "dark" ? "bg-gray-700" : "bg-gray-200"}`}>
              <div
                className={`h-full transition-all duration-500 ${percentage === 100 ? "bg-green-500" : "bg-blue-600"}`}
                style={{ width: `${percentage}%` }}
              />
            </div>

            {/* Per-step dot strip */}
            <div className="flex gap-1.5 mt-4">
              {([1, 2, 3, 4, 5, 6] as StepNumber[]).map((n) => {
                const s = data?.steps[String(n) as keyof typeof data.steps];
                const isComplete = s?.complete;
                return (
                  <div
                    key={n}
                    title={`Step ${n}: ${s?.label || ""}`}
                    className={`flex-1 h-1 rounded-full transition-colors ${
                      isComplete
                        ? "bg-green-500"
                        : expandedStep === n
                        ? "bg-blue-500"
                        : theme === "dark"
                        ? "bg-gray-700"
                        : "bg-gray-200"
                    }`}
                  />
                );
              })}
            </div>
          </div>

          {/* ─── Approval / status callout (if relevant) ────────────── */}
          {data?.registration?.status === "pending-approval" && (
            <div className={`rounded-2xl border p-4 mb-4 flex items-start gap-3 ${
              theme === "dark" ? "bg-amber-900/20 border-amber-800" : "bg-amber-50 border-amber-200"
            }`}>
              <Clock className={`w-5 h-5 flex-shrink-0 mt-0.5 ${theme === "dark" ? "text-amber-400" : "text-amber-600"}`} />
              <div className="flex-1">
                <p className={`text-sm font-semibold ${theme === "dark" ? "text-amber-300" : "text-amber-800"}`}>
                  {t("operator.setupGuide.pendingApprovalTitle", { defaultValue: "Pending admin approval" })}
                </p>
                <p className={`text-xs mt-1 ${theme === "dark" ? "text-amber-400" : "text-amber-700"}`}>
                  {t("operator.setupGuide.pendingApprovalDesc", {
                    defaultValue: "Your registration is being reviewed. You'll be notified once approved.",
                  })}
                </p>
              </div>
              <button
                onClick={fetchLifecycle}
                className={`text-xs font-medium hover:underline flex items-center gap-1 ${
                  theme === "dark" ? "text-amber-300" : "text-amber-700"
                }`}
              >
                <RefreshCw className="w-3 h-3" />
                {t("operator.setupGuide.refresh", { defaultValue: "Refresh" })}
              </button>
            </div>
          )}

          {data?.registration?.status === "rejected" && data.registration.rejectionReason && (
            <div className={`rounded-2xl border p-4 mb-4 ${
              theme === "dark" ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"
            }`}>
              <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${theme === "dark" ? "text-red-400" : "text-red-700"}`}>
                {t("operator.setupGuide.rejectionReasonLabel", { defaultValue: "Reason for rejection" })}
              </p>
              <p className={`text-sm ${theme === "dark" ? "text-red-300" : "text-red-800"}`}>
                {data.registration.rejectionReason}
              </p>
            </div>
          )}

          {data?.hotspot?.lastSeen && (
            <div className={`text-xs mb-4 ${theme === "dark" ? "text-gray-500" : "text-gray-500"}`}>
              {t("operator.setupGuide.lastSeen", {
                defaultValue: "Last activity from your hotspot: {{when}}",
                when: formatRelativeTime(data.hotspot.secondsSinceLastSeen),
              })}
            </div>
          )}

          {/* ─── Step cards ─────────────────────────────────────────── */}
          <div className="space-y-3">
            {([1, 2, 3, 4, 5, 6] as StepNumber[]).map((n) => {
              const stepData = data?.steps[String(n) as keyof typeof data.steps];
              const cfg = stepConfig[n];
              const isComplete = !!stepData?.complete;
              const isExpanded = expandedStep === n;
              const Icon = cfg.icon;

              // For steps 3 & 4, gate behind registration approval (locked otherwise)
              const isLocked = (n === 3 || n === 4) && !(data?.registration?.approved);

              return (
                <div
                  key={n}
                  className={`rounded-2xl border transition-all overflow-hidden ${
                    isComplete
                      ? theme === "dark"
                        ? "bg-[#333436] border-green-700/40 ring-1 ring-green-500/20"
                        : "bg-white border-green-300 ring-1 ring-green-500/20"
                      : isExpanded
                      ? theme === "dark"
                        ? "bg-[#333436] border-blue-700/50"
                        : "bg-white border-blue-300"
                      : theme === "dark"
                      ? "bg-[#333436] border-gray-800"
                      : "bg-white border-gray-200"
                  }`}
                >
                  {/* Header (always visible, click to expand) */}
                  <button
                    onClick={() => setExpandedStep(isExpanded ? null : n)}
                    className="w-full flex items-center gap-4 p-5 text-left"
                  >
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isComplete
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : isLocked
                          ? "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500"
                          : theme === "dark"
                          ? "bg-blue-600/10 text-blue-400"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      {isComplete ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : isLocked ? (
                        <Lock className="w-5 h-5" />
                      ) : (
                        <Icon className="w-6 h-6" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className={`text-xs font-bold uppercase tracking-wider ${
                          isComplete ? "text-green-600 dark:text-green-400" : theme === "dark" ? "text-gray-500" : "text-gray-400"
                        }`}>
                          {t("operator.setupGuide.stepLabel", { defaultValue: "Step" })} {n}
                        </span>
                        {cfg.optional && (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            theme === "dark" ? "bg-amber-900/30 text-amber-400" : "bg-amber-100 text-amber-700"
                          }`}>
                            {t("operator.setupGuide.optional", { defaultValue: "Optional" })}
                          </span>
                        )}
                        {isComplete && (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            theme === "dark" ? "bg-green-900/30 text-green-400" : "bg-green-100 text-green-700"
                          }`}>
                            {t("operator.setupGuide.complete", { defaultValue: "Complete" })}
                          </span>
                        )}
                        {isLocked && (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            theme === "dark" ? "bg-gray-800 text-gray-500" : "bg-gray-100 text-gray-500"
                          }`}>
                            {t("operator.setupGuide.locked", { defaultValue: "Unlocks after Step 1" })}
                          </span>
                        )}
                      </div>
                      <h3 className={`text-base font-bold ${theme === "dark" ? "text-white" : "text-gray-900"} truncate`}>
                        {cfg.title}
                      </h3>
                    </div>

                    <ChevronDown
                      className={`w-5 h-5 flex-shrink-0 transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      } ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}
                    />
                  </button>

                  {/* Expanded body */}
                  {isExpanded && (
                    <div className={`px-5 pb-5 pt-0 border-t ${theme === "dark" ? "border-gray-800" : "border-gray-100"}`}>
                      <p className={`text-sm leading-relaxed mb-4 mt-4 ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}>
                        {cfg.description}
                      </p>

                      {cfg.note && (
                        <div className={`text-xs font-mono px-3 py-2 rounded-lg mb-4 ${
                          theme === "dark" ? "bg-blue-900/20 text-blue-300 border border-blue-800/50" : "bg-blue-50 text-blue-700 border border-blue-100"
                        }`}>
                          💡 {cfg.note}
                        </div>
                      )}

                      <ul className="space-y-2 mb-4">
                        {cfg.checklist.map((item, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <div className={`w-4 h-4 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center ${
                              isComplete
                                ? "bg-green-500"
                                : theme === "dark"
                                ? "bg-gray-700"
                                : "bg-gray-200"
                            }`}>
                              {isComplete && <CheckCircle2 className="w-3 h-3 text-white" />}
                            </div>
                            <span className={`text-sm ${
                              isComplete
                                ? theme === "dark" ? "text-gray-400 line-through" : "text-gray-500 line-through"
                                : theme === "dark" ? "text-gray-300" : "text-gray-700"
                            }`}>
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* Per-step CTA */}
                      {cfg.action && !isComplete && !isLocked && (
                        <button
                          onClick={cfg.action.onClick}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
                        >
                          {cfg.action.label}
                          {n === 6 ? <ExternalLink className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                        </button>
                      )}

                      {/* Step-specific live data */}
                      {n === 2 && data?.hotspot && (
                        <>
                          {data.hotspot.ssid && (
                            <div className={`mt-4 px-3 py-2 rounded-lg text-xs font-mono ${
                              theme === "dark" ? "bg-gray-800 text-gray-300" : "bg-gray-100 text-gray-700"
                            }`}>
                              {t("operator.setupGuide.currentSsid", { defaultValue: "Current SSID" })}:{" "}
                              <span className="font-bold">{data.hotspot.ssid}</span>
                            </div>
                          )}
                          <SsidEditor
                            hotspotId={data.hotspot.id}
                            currentSsid={data.hotspot.ssid}
                            theme={theme}
                            onSaved={fetchLifecycle}
                          />
                        </>
                      )}
                      {n === 4 && data?.hotspot && (
                        <div className={`mt-4 text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                          {t("operator.setupGuide.status", { defaultValue: "Status" })}:{" "}
                          <span className={`font-semibold ${
                            data.hotspot.online ? "text-green-500" : "text-gray-500"
                          }`}>
                            {data.hotspot.online ? "Online" : data.hotspot.status}
                          </span>
                          {data.hotspot.lastSeen && (
                            <>
                              {" • "}
                              {t("operator.setupGuide.lastSeenInline", {
                                defaultValue: "last seen {{when}}",
                                when: formatRelativeTime(data.hotspot.secondsSinceLastSeen),
                              })}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ─── Footer note ────────────────────────────────────────── */}
          <div className={`text-xs text-center mt-8 ${theme === "dark" ? "text-gray-500" : "text-gray-500"}`}>
            {t("operator.setupGuide.footerNote", {
              defaultValue:
                "Steps marked Optional can be completed after your hotspot goes live. Need help? Contact KonectX Operator Support.",
            })}
          </div>
        </div>
      </div>
    </>
  );
}
